import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import { DeductionParams } from '@nxsys/store/slices/paymentSetting/types';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeductionForm, {
  DeductionFormRef,
  DeductionFormValues,
} from '../components/DeductionForm';
import AppLoader from '@nxsys/components/AppLoader';

type CreateDeductionProps = {
  isOpen: boolean;
  toggle: () => void;
  refetchData: () => void;
};

const CreateDeductionModal: React.FC<CreateDeductionProps> = ({
  isOpen,
  toggle,
  refetchData,
}: CreateDeductionProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<DeductionFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (values: DeductionFormValues) => {
    setIsSubmitting(true);
    let params: DeductionParams;
    // NX-5228: country decides the payload shape here too (same fix as
    // SettingsGeneral.tsx's handleSave) — an umbrella-Ireland tenant must get
    // the PRSI/USC/ASC/LPT toggles, not the UK-shaped beforeTax/beforeNI/beforePension
    // set it would fall into if `!isBureau` were checked first.
    if (isIreland) {
      params = {
        description: values.description ?? '',
        taxCanBeDeducted: Boolean(values.taxCanBeDeducted),
        employeePRSICanBeDeducted: Boolean(values.employeePRSICanBeDeducted),
        employerPRSICanBeDeducted: Boolean(values.employerPRSICanBeDeducted),
        uscCanBeDeducted: Boolean(values.uscCanBeDeducted),
        ascCanBeDeducted: Boolean(values.ascCanBeDeducted),
        pensionSchemaDeductionCanBeDeducted: Boolean(
          values.pensionSchemaDeductionCanBeDeducted,
        ),
        lptCanBeDeducted: Boolean(values.lptCanBeDeducted),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
      };
    } else if (!isBureau) {
      params = {
        description: values.description ?? '',
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        beforeTax: Boolean(values.beforeTax),
        beforeNI: Boolean(values.beforeNI),
        beforePension: Boolean(values.beforePension),
      };
    } else {
      params = {
        description: values.description ?? '',
        beforeTax: Boolean(values.beforeTax),
        beforeNI: Boolean(values.beforeNI),
        beforePension: Boolean(values.beforePension),
        includeInCisCalculation: false,
      };
    }
    dispatch(paymentSettingActions.createDeduction(params))
      .then(() => {
        setIsSubmitting(false);
        toggle();
        refetchData();
      })
      .catch((error) => {
        console.error(error);
        setIsSubmitting(false);
      });
  };

  return (
    <AppModalContainer
      title={messages['paymentSetting.deduction.addNewDeduction']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      {isSubmitting && <AppLoader />}
      <DeductionForm
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={{
          ...(!isBureau
            ? {
                beforeTax: false,
                beforeNI: false,
                beforePension: false,
                includeInCisCalculation: false,
              }
            : {}),
          ...(!isIreland
            ? {
                beforeTax: false,
                beforeNI: false,
                beforePension: false,
              }
            : {}),
          ...(isIreland
            ? {
                taxCanBeDeducted: true,
                employeePRSICanBeDeducted: true,
                employerPRSICanBeDeducted: true,
                uscCanBeDeducted: true,
                ascCanBeDeducted: true,
                pensionSchemaDeductionCanBeDeducted: true,
                includeInCisCalculation: false,
                lptCanBeDeducted: true,
              }
            : {}),
        }}
      />
    </AppModalContainer>
  );
};

export default CreateDeductionModal;
